const en = {
  nav: {
    home: 'Home',
    about: 'About',
    services: 'Services',
    projects: 'Projects',
    trust: 'Trust & Compliance',
    contact: 'Contact',
    partnerships: 'Partnerships',
    cta: 'Request a partnership call',
    menu: 'Menu',
  },
  hero: {
    badge: 'B2B · DACH markets · EU-ready',
    headline: 'Solar installation capacity you can schedule.',
    subheadline:
      'Spanish installation subcontractor for EPCs, solar companies, and energy firms across Europe. Two structured teams. Predictable throughput. Clean documentation.',
    cta_primary: 'Request a partnership call',
    cta_secondary: 'View our services',
    image_caption: 'On-site execution · HSE-first · Documentation-ready',
  },
  metrics: {
    title: 'Operational capacity',
    subtitle: 'Key figures for project planning and capacity allocation.',
    workers: { label: 'Installation workers', value: '6' },
    teams: { label: 'Independent teams', value: '2' },
    capacity: { label: 'Daily capacity', value: '~2' },
    capacity_unit: 'installs/day',
    experience: { label: 'Years in operation', value: '5+' },
  },
  services: {
    title: 'Services',
    subtitle: 'End-to-end installation services designed for solar operators, EPCs, and energy companies.',
    pv: {
      title: 'Residential PV Installations',
      description:
        'Complete residential solar installation from site assessment to commissioning. Crew coordination, mounting, wiring, testing, and as-built documentation included.',
      badges: ['B2B', 'SLA-ready', 'EU travel'],
      features: [
        'Crew scheduling & coordination',
        'On-site mounting & wiring',
        'Commissioning & testing',
        'As-built documentation',
      ],
    },
    ev: {
      title: 'EV Charger Installations',
      description:
        'Professional EV charging infrastructure installation with full coordination with licensed electricians, testing, and handover documentation. Ready for multi-site rollout programs.',
      badges: ['B2B', 'Multi-site', 'Rollout-ready'],
      features: [
        'Coordination with electricians',
        'Infrastructure installation',
        'Testing & commissioning',
        'Technical handover reports',
      ],
    },
    repairs: {
      title: 'Repairs & Incident Response',
      description:
        'Rapid on-site response to installation faults and system issues. Structured triage, resolution, and comprehensive photographic reporting for client records.',
      badges: ['SLA-ready', 'Fast response', 'Documented'],
      features: [
        'Priority triage & diagnosis',
        'On-site repair execution',
        'Photo documentation',
        'Written incident reports',
      ],
    },
    learn_more: 'View full service details',
  },
  why: {
    title: 'Why work with us',
    subtitle:
      'Built for EPCs and solar operators who need a reliable installation partner — not a local handyman.',
    principles: [
      {
        title: 'Two independent teams',
        desc: 'Run parallel installation schedules. Two fully equipped teams operating independently across different sites, enabling flexible and scalable deployment.',
      },
      {
        title: 'Structured documentation',
        desc: 'Every installation includes commissioning notes, as-built photographs, checklists, and handover reports — ready for your client delivery workflow.',
      },
      {
        title: 'EU-ready and mobile',
        desc: 'Based in Comunidad Valenciana with proven experience in Madrid and Barcelona. Available for Germany, Austria, Switzerland, and broader EU projects.',
      },
      {
        title: 'Proven track record',
        desc: 'Subcontracting experience with 1komma5°, Verne, EDP, and Holaluz — companies that set high standards for quality, compliance, and documentation.',
      },
    ],
    accordion: [
      {
        title: 'HSE compliance',
        content:
          'All teams operate under strict health and safety protocols. PPE requirements, fall protection, and site safety documentation are standard procedure on every job — not optional extras.',
      },
      {
        title: 'Documentation standards',
        content:
          'We deliver complete as-built documentation on every project: installation photographs, commissioning reports, electrical diagrams (when applicable), and signed client checklists.',
      },
      {
        title: 'Equipment and tooling',
        content:
          'Teams arrive fully equipped with professional installation tools, safety equipment, and testing instruments. No dependency on client-provided equipment.',
      },
    ],
  },
  partners: {
    title: 'Clients and partners',
    subtitle: 'We have worked as installation subcontractors for:',
  },
  gallery_preview: {
    title: 'Selected projects',
    subtitle: 'A selection of recent residential and commercial installation work.',
    view_all: 'View all projects',
  },
  cta_section: {
    title: 'Ready to scale your installation capacity?',
    subtitle:
      'Talk to us about your upcoming projects in Spain, Germany, Austria, or Switzerland. We can typically mobilise within a week.',
    cta: 'Request a partnership call',
    secondary: 'or email us at info@energiesse.com',
  },
  about: {
    headline: 'A reliable installation partner, purpose-built for B2B operations.',
    intro:
      'ENERGIESSE SL is a Spanish solar installation subcontractor specialising in residential PV systems, EV charger installations, and rapid incident response. We work exclusively with solar companies, EPCs, and energy operators.',
    team_title: 'Team structure',
    team_subtitle:
      'Two independent installation teams, each capable of operating on separate sites simultaneously.',
    team_1: {
      title: 'Installation Team A',
      role: 'Primary PV installations',
      desc: 'Specialist team for new residential PV systems. Full cycle: site assessment, mounting, wiring, commissioning, and documentation.',
    },
    team_2: {
      title: 'Installation Team B',
      role: 'EV & incident response',
      desc: 'Specialist team for EV charger installations and rapid incident response. Available for parallel deployment alongside Team A.',
    },
    capacity_title: 'Operational capacity',
    capacity_items: [
      { label: 'Total workers', value: '6 trained specialists' },
      { label: 'Team structure', value: '2 independent teams of 3' },
      { label: 'Daily throughput', value: '~2 residential installations/day' },
      { label: 'Scalability', value: 'Teams deployable to separate regions' },
    ],
    geography_title: 'Geographic coverage',
    geography_primary: 'Primary region',
    geography_primary_value: 'Comunidad Valenciana, Spain',
    geography_secondary: 'Established coverage',
    geography_secondary_value: 'Madrid, Barcelona',
    geography_international: 'International availability',
    geography_international_value: 'Germany, Austria, Switzerland and broader EU',
    compliance_title: 'Professional positioning',
    compliance_desc:
      'ENERGIESSE SL operates exclusively as a B2B installation subcontractor. We are not a consumer-facing company. Our documentation, process standards, and commercial terms are designed for professional operators managing multi-site installation programmes.',
    values_title: 'Our operating principles',
    values: [
      { title: 'Reliability', desc: 'We commit to schedules and deliver. If circumstances change, we communicate early.' },
      { title: 'Documentation', desc: 'Every project leaves a complete paper trail. No exceptions.' },
      { title: 'Safety', desc: 'HSE compliance is not optional. It is our baseline on every site.' },
      { title: 'Clarity', desc: 'Clear pricing, clear timelines, clear communication. No surprises.' },
    ],
  },
  services_page: {
    headline: 'Installation services for professional operators.',
    subtitle: 'Three core service areas, each designed for B2B subcontracting engagements. SLA-ready, documented, and scalable.',
    pv: {
      headline: 'Residential PV Installations',
      intro: 'Our primary service. Full-cycle residential photovoltaic installation for EPCs and solar operators running rollout programmes across Spain and Europe.',
      scope: 'Scope of service',
      scope_items: [
        'Pre-installation site assessment and documentation',
        'Panel mounting and roof structure preparation',
        'DC wiring and string configuration',
        'Inverter installation and AC connection',
        'System commissioning and performance verification',
        'As-built documentation and client handover package',
      ],
      b2b_note: 'We operate as a white-label installation partner. Handover documentation can be branded for your client delivery workflow.',
    },
    ev: {
      headline: 'EV Charger Installations',
      intro: 'Professional EV charger installation for residential and commercial properties. Ready for multi-site rollout programmes with consistent quality and documentation.',
      scope: 'Scope of service',
      scope_items: [
        'Site survey and infrastructure assessment',
        'EVSE unit installation and wall mounting',
        'Cable routing and electrical connection',
        'Coordination with licensed electricians for grid connection',
        'Testing, load verification, and commissioning',
        'Full handover documentation and user briefing notes',
      ],
      b2b_note: 'Suitable for operators running installation programmes with recurring volume commitments. Framework agreements available.',
    },
    repairs: {
      headline: 'Repairs & Incident Response',
      intro: 'On-site diagnostic and repair service for PV and EV charging systems. Structured response for EPCs managing warranty claims, client escalations, or system underperformance.',
      scope: 'Scope of service',
      scope_items: [
        'Priority response booking (typically within 48–72 hours)',
        'On-site diagnostic and root cause identification',
        'Component repair or replacement',
        'Performance re-verification post-repair',
        'Photographic and written incident report',
        'Escalation protocol for complex faults',
      ],
      b2b_note: 'Incident reports are structured for direct inclusion in your client communication and warranty tracking systems.',
    },
    common: {
      b2b_badge: 'B2B only',
      geographic_note: 'Service coverage: Spain (primary) · Germany, Austria, Switzerland, EU (by arrangement)',
      contact_cta: 'Request a scope discussion',
    },
  },
  projects_page: {
    headline: 'Installation portfolio',
    subtitle: 'Selected residential and commercial installation projects. All projects delivered as B2B subcontractor work.',
    filter_all: 'All',
    filter_pv: 'PV installations',
    filter_ev: 'EV chargers',
    filter_repairs: 'Repairs',
    before: 'Before',
    after: 'After',
    video_placeholder: 'Installation video — available on request',
    projects: [
      {
        id: 'p1',
        title: 'Residential PV — Valencia',
        type: 'pv',
        capacity: '6.5 kWp',
        panels: '13 panels',
        location: 'Comunidad Valenciana',
        year: '2024',
        client: 'EPC partner (confidential)',
        before_img: 'https://images.unsplash.com/photo-1605980776566-0486c3ac7617?w=800&q=80',
        after_img: 'https://images.unsplash.com/photo-1509391366360-2e959784a276?w=800&q=80',
      },
      {
        id: 'p2',
        title: 'Residential PV — Madrid',
        type: 'pv',
        capacity: '8.2 kWp',
        panels: '16 panels',
        location: 'Madrid',
        year: '2024',
        client: 'EPC partner (confidential)',
        before_img: 'https://images.unsplash.com/photo-1558449907-29a1048b85b9?w=800&q=80',
        after_img: 'https://images.unsplash.com/photo-1466611653911-95081537e5b7?w=800&q=80',
      },
      {
        id: 'p3',
        title: 'Multi-unit PV — Barcelona',
        type: 'pv',
        capacity: '24 kWp',
        panels: '48 panels',
        location: 'Barcelona',
        year: '2023',
        client: 'Solar operator (confidential)',
        before_img: 'https://images.unsplash.com/photo-1497440001374-f26997328c1b?w=800&q=80',
        after_img: 'https://images.unsplash.com/photo-1508514177221-188b1cf16e9d?w=800&q=80',
      },
      {
        id: 'p4',
        title: 'EV Charger Installation — Valencia',
        type: 'ev',
        capacity: '11 kW',
        panels: '1 unit',
        location: 'Comunidad Valenciana',
        year: '2024',
        client: 'Energy operator (confidential)',
        before_img: 'https://images.unsplash.com/photo-1593941707882-a5bba13c2e09?w=800&q=80',
        after_img: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800&q=80',
      },
      {
        id: 'p5',
        title: 'Incident response — Madrid',
        type: 'repairs',
        capacity: '5.5 kWp',
        panels: '11 panels',
        location: 'Madrid',
        year: '2023',
        client: 'EPC partner (confidential)',
        before_img: 'https://images.unsplash.com/photo-1595437193398-f24279553f4f?w=800&q=80',
        after_img: 'https://images.unsplash.com/photo-1564151436289-8fc3f8ea6326?w=800&q=80',
      },
      {
        id: 'p6',
        title: 'Residential PV — Valencia (large)',
        type: 'pv',
        capacity: '12 kWp',
        panels: '24 panels',
        location: 'Comunidad Valenciana',
        year: '2024',
        client: 'Solar operator (confidential)',
        before_img: 'https://images.unsplash.com/photo-1548348384-a1069e9d4179?w=800&q=80',
        after_img: 'https://images.unsplash.com/photo-1552197892-f2ad2f75e7c8?w=800&q=80',
      },
    ],
  },
  trust: {
    headline: 'Trust & Compliance',
    subtitle: 'Legal standing, insurance coverage, and professional certifications — available for partner due diligence.',
    legal_title: 'Legal entity',
    legal_fields: [
      { label: 'Company name', value: 'ENERGIESSE SL' },
      { label: 'Legal form', value: 'Sociedad Limitada (SL)' },
      { label: 'CIF / VAT', value: 'Available upon request' },
      { label: 'Registered address', value: 'Comunidad Valenciana, Spain' },
      { label: 'Domain', value: 'energiesse.com' },
      { label: 'Primary contact', value: 'info@energiesse.com' },
    ],
    insurance_title: 'Insurance coverage',
    insurance_items: [
      { label: 'Civil liability insurance', status: 'Active', detail: 'Coverage for third-party damages during on-site operations' },
      { label: 'Professional indemnity', status: 'Active', detail: 'Coverage for installation defects and commissioning errors' },
      { label: "Workers' accident insurance", status: 'Active', detail: 'Full coverage for all installation team members' },
    ],
    cert_title: 'Certifications & training',
    cert_items: [
      { label: 'Photovoltaic installer certification', status: 'Current', issuer: 'Spanish renewable energy authority' },
      { label: 'EV charger installer qualification', status: 'Current', issuer: 'Accredited training provider' },
      { label: 'Low-voltage electrical installation', status: 'Current', issuer: 'Spanish electrical authority' },
      { label: 'HSE / Workplace safety training', status: 'Current', issuer: 'Occupational safety authority' },
    ],
    safety_title: 'Safety standards',
    safety_desc:
      'All installation teams operate under strict HSE protocols. Site safety documentation, PPE compliance plans, and fall protection procedures are enforced on every project site, regardless of size or duration. Safety records and incident history are available upon request for due diligence purposes.',
    docs_title: 'Documentation deliverables',
    docs_intro: 'Every completed project generates the following documentation package, delivered to the commissioning party:',
    docs_items: [
      'Commissioning report (signed)',
      'As-built photographic record',
      'Electrical single-line diagram (where applicable)',
      'Client sign-off checklist',
      'Incident or deviation report (where applicable)',
    ],
    request_docs: 'Request compliance documentation',
    request_docs_note: 'Insurance certificates and certification documents available upon signed NDA or for pre-qualified partners.',
    active: 'Active',
    current: 'Current',
  },
  contact: {
    headline: 'Request a partnership call',
    subtitle:
      'Tell us about your installation requirements. We respond to all B2B inquiries within one business day.',
    email_label: 'Direct contact',
    email_value: 'info@energiesse.com',
    response_note: 'Response within 1 business day',
    form_title: 'Partnership inquiry',
    form_company: 'Company name',
    form_company_placeholder: 'Your company name',
    form_name: 'Contact person',
    form_name_placeholder: 'Your full name',
    form_email: 'Business email',
    form_email_placeholder: 'you@company.com',
    form_phone: 'Phone (optional)',
    form_phone_placeholder: '+49 ...',
    form_country: 'Country',
    form_country_placeholder: 'Select your country',
    form_project_type: 'Project type',
    form_project_type_placeholder: 'Select project type',
    form_message: 'What do you need from an installation partner?',
    form_message_placeholder:
      'Describe your installation programme: volume, regions, timeline, and any specific requirements.',
    form_consent:
      'I confirm this is a B2B inquiry and agree to be contacted regarding this request.',
    form_submit: 'Request a partnership call',
    form_success_title: 'Inquiry received',
    form_success:
      'Thank you. We have received your inquiry and will respond within one business day.',
    form_error:
      'There was an issue submitting your inquiry. Please try again or contact us directly at info@energiesse.com.',
    project_types: [
      'Residential PV rollout',
      'EV charger rollout',
      'Repairs & maintenance programme',
      'Mixed / to be discussed',
    ],
    countries: [
      'Germany',
      'Austria',
      'Switzerland',
      'Spain',
      'Other EU country',
    ],
    validation: {
      company_required: 'Company name is required',
      name_required: 'Contact person name is required',
      email_required: 'Business email is required',
      email_invalid: 'Please enter a valid email address',
      country_required: 'Please select your country',
      project_type_required: 'Please select a project type',
      message_required: 'Please describe your requirements',
      consent_required: 'Consent is required to submit this form',
    },
  },
  partnerships: {
    headline: 'Partner with ENERGIESSE SL',
    subtitle:
      'We are actively seeking subcontracting and partnership agreements with solar installers, EPCs, and energy operators across Germany, Austria, and Switzerland.',
    offer_title: 'What we offer as a subcontracting partner',
    offer_items: [
      {
        title: 'Dedicated installation capacity',
        desc: 'Reserve installation capacity for your project pipeline. Two independent teams available for parallel scheduling, enabling multi-site execution.',
      },
      {
        title: 'Complete handover documentation',
        desc: 'Every project includes commissioning reports, as-built photographs, and client checklists — ready for your delivery workflow or direct client handover.',
      },
      {
        title: 'EU mobility and flexibility',
        desc: 'Based in Spain, available across Europe. We can mobilise for projects in Germany, Austria, Switzerland, and other EU countries with short lead times.',
      },
      {
        title: 'Proven reliability with industry operators',
        desc: 'Track record working with 1komma5°, Verne, EDP, and Holaluz. We understand the quality and documentation standards required by large-scale operators.',
      },
    ],
    process_title: 'How a partnership works',
    process_steps: [
      {
        step: '01',
        title: 'Initial discovery call',
        desc: 'We discuss your project pipeline, capacity requirements, regions, and timeline. No commitment required.',
      },
      {
        step: '02',
        title: 'Proposal',
        desc: 'We submit a capacity and commercial proposal tailored to your programme. Typically delivered within 2 working days.',
      },
      {
        step: '03',
        title: 'Agreement',
        desc: 'We formalise a subcontracting agreement or framework contract covering scope, rates, documentation, and SLAs.',
      },
      {
        step: '04',
        title: 'First deployment',
        desc: 'Teams mobilise for your first projects. Documentation delivered at each completion. Performance review after initial run.',
      },
    ],
    requirements_title: 'Ideal partner profile',
    requirements_items: [
      'Solar EPC or installation company active in DACH or Spain',
      'Project pipeline of 5+ installations per month or recurring programme',
      'Requires reliable documentation and white-label installation delivery',
      'Values speed, consistency, and clear communication',
    ],
    cta_title: 'Start the conversation',
    cta_subtitle: 'Reach out with your requirements. We respond within one business day.',
    cta: 'Request a partnership call',
    faq_title: 'Frequently asked questions',
    faqs: [
      {
        q: 'Do you work outside Spain?',
        a: 'Yes. Our primary operations are in Comunidad Valenciana, with established experience in Madrid and Barcelona. We are available for international projects in Germany, Austria, Switzerland, and other EU countries. International projects are arranged on a case-by-case basis.',
      },
      {
        q: 'Can you handle high installation volumes?',
        a: 'Our standard capacity is approximately 2 residential installations per day with our current two teams. For higher volumes or time-sensitive programmes, we can discuss capacity scaling or dedicated allocation.',
      },
      {
        q: 'What documentation do you deliver?',
        a: 'Every completed installation includes: commissioning report, as-built photographs, electrical diagrams (where applicable), and a signed client checklist. Reports can be formatted for your client delivery workflow.',
      },
      {
        q: 'Do you operate as a white-label partner?',
        a: 'Yes. We operate as a silent subcontractor where required. Documentation, uniforms, and communication can be aligned to your brand requirements.',
      },
    ],
  },
  footer: {
    company: 'ENERGIESSE SL',
    domain: 'energiesse.com',
    email: 'info@energiesse.com',
    tagline: 'B2B solar installation subcontractor · Spain · EU',
    legal: 'Legal notice',
    privacy: 'Privacy policy',
    rights: 'All rights reserved.',
    nav_title: 'Navigation',
    contact_title: 'Contact',
    legal_title: 'Legal',
    address: 'Comunidad Valenciana, Spain',
  },
  language_changed: 'Language set to English',
  common: {
    loading: 'Sending...',
    back: 'Back',
    read_more: 'Read more',
    close: 'Close',
  },
};

export default en;
